import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, EmptyState, vars } from "@pack-it-up/ui-web";
import type { TemplateSummary, TripSummary } from "@pack-it-up/core";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { useDayTick } from "../../lib/useDayTick.js";
import { SearchBox, matchesName, useSearch } from "../shared/SearchBox.js";
import { SectionHead } from "../shared/SectionHead.js";
import { TripGrid } from "../shared/TripGrid.js";
import { TemplateGrid } from "../shared/TemplateGrid.js";
import { NewTemplateModal } from "./NewTemplateModal.js";
import { NewTripModal } from "./NewTripModal.js";

const partOfDay = (now: Date = new Date()): string => {
  const h = now.getHours();
  if (h < 12) return "morning";
  if (h < 18) return "afternoon";
  return "evening";
};

// The signed-in landing page: a compact topbar + scrollable content area.
export const HomePage = (): ReactNode => {
  const { auth } = useStores();
  const { home, pendingTemplateDelete, pendingTripDelete } = useSession();
  const navigate = useNavigate();
  const user = useStoreValue(auth, (s) => s.user);
  const isAnonymous = user?.isAnonymous ?? false;
  const trips = useStoreValue(home, (s) => s.trips);
  const rawTemplates = useStoreValue(home, (s) => s.templates);
  // The delete write is deferred behind an Undo toast (AppShell), so `home.templates`/
  // `groups` still have it — hide it here until the write actually lands (`hiddenIds`
  // outlives the toast itself, so nothing flickers back into view while it's in flight).
  const hiddenTemplateIds = useStoreValue(pendingTemplateDelete, (s) => s.hiddenIds);
  const templates = useMemo(
    () => rawTemplates.filter((t) => !hiddenTemplateIds.includes(t.id)),
    [rawTemplates, hiddenTemplateIds],
  );
  const hiddenTripIds = useStoreValue(pendingTripDelete, (s) => s.hiddenIds);
  const dayTick = useDayTick();
  const groups = useMemo(() => home.getState().groups(), [home, trips, dayTick]);

  // The greeting shrinks in stages as the topbar narrows, so it never gets ellipsized:
  // 0 = full name, 1 = first name only, 2 = "Hello, <first name>", 3 = icon only,
  // 4 = empty. A resize resets to stage 0 so the greeting grows back when there's room
  // again; the layout effect then re-measures and steps back up as needed.
  const topRef = useRef<HTMLDivElement>(null);
  const greetingRef = useRef<HTMLHeadingElement>(null);
  const [greetingStage, setGreetingStage] = useState(0);
  // Resetting greetingStage to 0 on resize is a no-op (and so triggers no re-render,
  // skipping the re-measure below) whenever it's already 0 — e.g. shrinking from a wide
  // window that never needed to degrade. This tick forces a render on every resize
  // regardless, so the layout effect always gets a chance to re-check overflow.
  const [, setResizeTick] = useState(0);

  useEffect(() => {
    const el = topRef.current;
    if (el === null) return;
    const ro = new ResizeObserver(() => {
      setGreetingStage(0);
      setResizeTick((t) => t + 1);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = greetingRef.current;
    if (el === null) return;
    if (el.scrollWidth > el.clientWidth && greetingStage < 4) {
      setGreetingStage(greetingStage + 1);
    }
  });

  const firstName = user?.displayName.split(" ")[0] ?? "";

  const { query, filtered: filteredQuery, onChange: handleSearch } = useSearch();

  // Doc 10 §2: dateless trips are "active/plannable", shown after dated upcoming ones in
  // the same section. Past and archived trips live on My Trips, not Home (issue #163).
  const activeTrips = useMemo(
    () => [...groups.upcoming, ...groups.dateless].filter((t) => !hiddenTripIds.includes(t.id)),
    [groups, hiddenTripIds],
  );
  const shownUpcoming =
    filteredQuery === "" ? activeTrips : activeTrips.filter((t: TripSummary) => matchesName(t.name, filteredQuery));
  const shownTemplates =
    filteredQuery === "" ? templates : templates.filter((t: TemplateSummary) => matchesName(t.name, filteredQuery));

  const openTrip = (id: string): void => {
    navigate(`/trip/${id}`);
  };

  const [showNewTrip, setShowNewTrip] = useState(false);
  const [showNewTemplate, setShowNewTemplate] = useState(false);

  return (
    <>
      {/* Compact topbar: greeting + search */}
      <div
        ref={topRef}
        style={{
          height: "56px",
          borderBottom: `1px solid ${vars.color.border}`,
          display: "flex",
          alignItems: "center",
          padding: `0 ${vars.space.xl}`,
          gap: vars.space.md,
          background: vars.color.background,
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <h1
          ref={greetingRef}
          style={{
            fontFamily: vars.font.heading,
            fontWeight: vars.font.weightBold,
            fontSize: "17px",
            letterSpacing: vars.font.trackingSnug,
            margin: 0,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {greetingStage < 3 && (
            <>
              {greetingStage >= 2 ? "Hello" : `Good ${partOfDay()}`}
              {user !== null && !isAnonymous && (
                <>
                  ,{" "}
                  <span style={{ color: vars.color.accent }}>{greetingStage >= 1 ? firstName : user.displayName}</span>
                </>
              )}{" "}
            </>
          )}
          {greetingStage < 4 && "✈"}
        </h1>

        <SearchBox value={query} onChange={handleSearch} placeholder="Search trips & templates…" />
      </div>

      {/* Scrollable content area */}
      <div style={{ padding: `${vars.space.xl} ${vars.space.xl} calc(${vars.space.xl} * 2)` }}>
        {/* Upcoming Trips section */}
        <section>
          <SectionHead
            title="Upcoming Trips"
            count={`${activeTrips.length} ${activeTrips.length === 1 ? "trip" : "trips"}`}
          />

          {activeTrips.length === 0 && trips.length === 0 ? (
            <EmptyState
              title="No trips yet"
              description="Plan your first trip to start packing."
              {...(isAnonymous ? {} : { action: <Button onClick={() => setShowNewTrip(true)}>New trip</Button> })}
            />
          ) : (
            <TripGrid trips={shownUpcoming} onOpen={openTrip} {...(isAnonymous ? {} : { onNew: () => setShowNewTrip(true) })} />
          )}
        </section>

        {/* Templates section — Doc 10 §1: Home shows upcoming trips and templates only;
            past and archived trips/templates live on My Trips / My Templates. */}
        <section>
          <SectionHead
            title="Templates"
            count={`${templates.length} saved`}
            link={
              <Link
                to="/templates"
                style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted, display: "flex", alignItems: "center", gap: vars.space.xxs }}
              >
                Browse all
              </Link>
            }
          />
          {shownTemplates.length === 0 && templates.length === 0 ? (
            <p style={{ color: vars.color.textMuted, marginBottom: vars.space.md }}>No templates yet.</p>
          ) : shownTemplates.length === 0 ? (
            <p style={{ color: vars.color.textMuted, marginBottom: vars.space.md }}>No templates match your search.</p>
          ) : null}
          <TemplateGrid
            templates={shownTemplates}
            onOpen={(id) => navigate(`/template/${id}`)}
            {...(filteredQuery === "" && !isAnonymous ? { onNew: () => setShowNewTemplate(true) } : {})}
          />
        </section>
      </div>

      <NewTripModal open={showNewTrip} onClose={() => setShowNewTrip(false)} />
      <NewTemplateModal open={showNewTemplate} onClose={() => setShowNewTemplate(false)} />
    </>
  );
};
